/**
 * Hero Immersion — 沉浸底色層高度量測 + FX 發散中心量測 + nav 兩態切換
 *
 * 配合 .hero-immersion（src/styles/components/hero-immersion.css）與 hero-slider.js：
 *
 * 1. 高度量測（--hero-immersion-height）
 *    immersion 層需精準覆蓋「頁頂 → hero 底緣」。因 top-bar 可被關閉（高度變動）+
 *    mobile hero 為 auto 高度，無法用純 CSS 算 → JS 於 load / resize 量 hero 底緣的
 *    文件座標（getBoundingClientRect().bottom + scrollY）寫入 :root。
 *    量測前 CSS 以 100vh 墊底，避免首屏白邊。
 *
 * 2. FX 發散中心量測（--hero-fx-origin-x/y）
 *    FX（漣漪/掃描/雨滴）以 3D icon 中心為發散原點。三 slide icon 位置可能不同 →
 *    切 slide 時 hero-slider.js 發 hero:slidechange（detail.index），本檔對「該 index 的
 *    icon」重量一次寫入 origin（治舊版發散中心錯位在舊 slide icon 上）。亦於 load/resize 量。
 *    .hero-immersion top:0/left:0（containing block = body）→ 文件座標可直接當 FX 座標。
 *
 * 3. nav 兩態切換（.site-nav.is-immersive）
 *    沿用 ai-fab 的 IntersectionObserver 模式（獨立 observer，不干擾 ai-fab）。
 *    observe .hero，rootMargin top 取 nav 高度負值 → nav「即將離開深底前」就換回實底態，
 *    避免白字壓白底瞬間。index.html 的 .site-nav 已預掛 is-immersive 防 FOUC。
 *
 * 4. 視口保底網（belt-and-suspenders，骨架保留、未接線 — 見下方 computeHeroFloor）
 *    純判斷核心 computeHeroFloor 曾規劃在偵測到「lvh 解析偏短」時把 .hero/.hero-immersion 頂到多候選視口高
 *    取 max，治 iOS 工具列收合/展開造成的視口波動。同款「填不滿露出下一塊」症狀 07-02 鑑識已歸因 Safari
 *    啟發式快取、以 cache header 根治；07-08 一度接線後在可視視口縮小時反而把 hero 撐過頭、造成下方多餘
 *    留白，故拔線。computeHeroFloor 保留純函式骨架（供 tests/unit 單元測試、未來新真機證據回報時復用）；
 *    DOM 讀寫呼叫端（enforceHeroViewportFloor 等）已移除。
 *
 * 底色流動：v4 改 CSS @property 原生補間（見 hero-immersion.css），JS 不再寫 inline background。
 * reduced-motion：CSS 已將兩態 transition 退化為瞬切；本檔照常 toggle class，行為不變。
 */

/**
 * computeHeroFloor — 視口保底網的純判斷核心（無副作用、不碰 DOM、不讀全域，可單元測試）。
 *
 * 職責：吃「當前量測值（多候選視口高 + isIOS 旗標）+ 歷來棘輪狀態」，回「要不要介入 / 頂到多高 /
 *   更新後的棘輪值」。DOM 讀寫、候選值量測與 isIOS 偵測全留在下方 enforceHeroViewportFloor（呼叫端）。
 *
 * 【為何多候選取 max（v2 修 innerHeight 盲點）】
 *   v1 參考值單用 innerHeight：iOS 首次載入 Safari 工具列「展開」→ innerHeight = 偏短小視口
 *   （≈ lvh 偏短後的 hero 高、甚至更矮）→ target - tolerance 永不大於 heroHeight → 量不出短缺、guard 空轉，
 *   放任 quick-nav 白底圖示列從第一屏底部露出 30–40px。visualViewport.height 同病（跟著縮）；
 *   documentElement.clientHeight（ICB）在 lvh 偏短假設下大概率一起偏短，不可靠、不採用。
 *   修法＝參考值「多候選取 max」：
 *     - innerHeight、visualViewportHeight：恆納入（正常環境 ≈ 真實視口 ≈ rendered → 不介入、inert）。
 *     - screenPortrait（= Math.max(screen.width, screen.height)，恆為直向長邊）：**僅 iOS 納入**。
 *       載入當下唯一不受工具列態影響的全螢幕信號（iOS 上為 CSS px、恆 portrait 值；iPhone 16 = 852）。
 *   ⚠ screen 候選 iOS 限定的原因：Android Chrome 的 screen.height 含系統 UI、> 正確 lvh；不 gate 會讓
 *     guard 在「行為正確」的 Android 上誤撐 24–48px、違反「正常環境完全 inert」核心設計。故 isIOS=false 時
 *     screenPortrait 一律不納入候選（呼叫端仍量、由本純函式依旗標決定用不用，維持零 DOM 依賴、可測試）。
 *
 * 棘輪（grow-only）：target = max(歷來 ratchetMax, 本輪候選 max)，只升不降。iOS Safari 工具列收合/展開
 *   讓候選視口高波動；鎖住見過的最大值杜絕 hero 高度來回抖動。orientationchange 時基準翻轉 →
 *   呼叫端把 ratchetMax 歸零重置（見下方 orientationchange listener）。
 *
 * 介入條件：rendered（純 CSS 驅動的 hero 高，量測前呼叫端已暫清自己的 inline 值）短於 target 超過容差
 *   → 判定 CSS 100lvh 這輪「解析偏短」，回 floor = ceil(target) 供頂高。
 *   正常環境（lvh 正確：rendered ≈ 真實視口，含桌機 / Playwright / Android / 正常 iOS）永不滿足 →
 *   intervene:false → 完全 inert。
 *   退化輸入（所有候選為 0 / NaN / 非數，如尚未佈局或不支援環境）→ 有效候選為空 → observed=0 → target 非正
 *   → 條件不成立 → inert（不誤撐、不拋錯，fail-inert）。
 *
 * @param {object} m
 * @param {number}  m.heroHeight             純 CSS 驅動的 hero rendered 高（getBoundingClientRect().height）
 * @param {number}  m.innerHeight            window.innerHeight（iOS 工具列態影響，首載可能偏短）
 * @param {number} [m.visualViewportHeight]  window.visualViewport.height（同 innerHeight 病，恆納入取 max）
 * @param {number} [m.screenPortrait]        Math.max(screen.width, screen.height) 直向長邊；僅 isIOS 時納入候選
 * @param {boolean}[m.isIOS=false]           是否 iOS（呼叫端 UA/maxTouchPoints/platform 偵測）；gate screenPortrait
 * @param {number} [m.ratchetMax=0]          歷來觀測候選 max 的峰值；orientationchange 後為 0
 * @param {number} [m.tolerance=2]           短缺容差（px），吸收次像素捨入、避免臨界抖寫
 * @returns {{ intervene: boolean, floor: number|null, ratchetMax: number }}
 */
export function computeHeroFloor({
  heroHeight,
  innerHeight,
  visualViewportHeight,
  screenPortrait,
  isIOS = false,
  ratchetMax = 0,
  tolerance = 2,
}) {
  // 候選視口高：innerHeight + visualViewportHeight 恆納入；screenPortrait 僅 iOS 納入
  // （Android screen.height 含系統 UI > 真實 lvh，納入會誤撐、破壞「正常環境 inert」）。
  const candidates = [innerHeight, visualViewportHeight];
  if (isIOS) candidates.push(screenPortrait);
  // 濾非正 / NaN / 非數（退化輸入 fail-inert：尚未佈局 / 不支援環境 / 未傳值）。
  const valid = candidates.filter((v) => typeof v === 'number' && v > 0);
  const observed = valid.length ? Math.max(...valid) : 0;
  const target = Math.max(ratchetMax, observed);
  const intervene = target > 0 && heroHeight < target - tolerance;
  return {
    intervene,
    floor: intervene ? Math.ceil(target) : null,
    ratchetMax: target,
  };
}

const immersion = document.querySelector('.hero-immersion');
const heroEl = document.querySelector('.hero');
const nav = document.querySelector('.site-nav');

if (immersion && heroEl) {
  // ----- 1. 高度量測 -----
  const root = document.documentElement;

  // FX 發散中心：量某個 icon 中心的文件座標寫入 --hero-fx-origin-x/y。
  // .hero-immersion 為 position:absolute / top:0 / left:0（containing block = body），
  // 其座標原點即文件左上角，故文件座標可直接當 FX layer 的座標使用。
  // index 指定時量該 slide 的 icon（切 slide 用，避免量到正在淡出的舊 slide）；
  // 未指定則取當前可見 slide 的 icon（[hidden] slide rect 為 0），fallback 第一個。
  const measureOrigin = (index) => {
    const icon =
      (typeof index === 'number'
        ? document.querySelector(`.hero__content[data-slide-content="${index}"] .hero__icon`)
        : null) ||
      document.querySelector('.hero__content:not([hidden]) .hero__icon') ||
      document.querySelector('.hero__icon');
    if (!icon) return;
    const r = icon.getBoundingClientRect();
    // icon 在 [hidden] 或尚未排版時 rect 為 0 → 不寫，沿用 CSS fallback / 上次有效值
    if (r.width === 0 && r.height === 0) return;
    root.style.setProperty('--hero-fx-origin-x', `${Math.round(r.left + r.width / 2 + window.scrollX)}px`);
    root.style.setProperty('--hero-fx-origin-y', `${Math.round(r.top + r.height / 2 + window.scrollY)}px`);
  };

  const measureHeight = () => {
    const rect = heroEl.getBoundingClientRect();
    // hero 底緣的文件座標 = viewport 內 bottom + 當前捲動位移
    const heroBottom = rect.bottom + window.scrollY;
    root.style.setProperty('--hero-immersion-height', `${Math.round(heroBottom)}px`);
    // hero 上方 chrome 總高（top-bar + nav + search）：scroll-top 時 = hero 文件座標 top。
    // 供手機滿版 hero 的 min-height: calc(100svh - var(--hero-chrome-offset)) 精準扣除；
    // CSS fallback（375 寬實測值）讓 pre-JS 值 ≈ 本 JS 值 → 不觸發 resize CLS。
    // top-bar 關閉時既有 MutationObserver 會重跑本函式 → chrome 變矮、hero 自動長回。
    const heroTop = rect.top + window.scrollY;
    root.style.setProperty('--hero-chrome-offset', `${Math.round(heroTop)}px`);
    measureOrigin();
  };

  // hero 是否在視窗內（由下方 nav IntersectionObserver 更新）。初始於頁頂、hero 可見 → true。
  // 用途：off-screen autoplay tick 加固——hero 捲離畫面時 nav 已切 fixed（out-of-flow），此時
  // 量到的 icon 文件-Y 會少掉 ≈navHeight（stale），不可持久化；捲回時 IO 進 immersive 分支會重量校正。
  // 無 nav / 不支援 IO 時恆為 true → 退化回原本「每次 slidechange 都重量」行為，graceful。
  let isHeroIntersecting = true;

  // load（圖片 / 字型 metrics 影響 hero 高度，load 後量最準）+ DOMContentLoaded 先粗量一次
  measureHeight();
  window.addEventListener('load', measureHeight);

  // 切 slide：對「新 slide 的 icon」重量發散中心（hero-slider.js 發 hero:slidechange）。
  // 新塊在事件發出前已 hidden=false（可量），用 detail.index 鎖定避開正在淡出的舊塊。
  // detail.settled=true（換場收尾再發一次）時舊塊已 hidden、新塊回到最終位置 → 校正
  //   mobile 單欄流佈局換場期間「新舊兩塊同時在流」造成的 y 暫時偏移（桌機共用中心，兩次量值相同）。
  heroEl.addEventListener('hero:slidechange', (e) => {
    // off-screen 加固：hero 不在視窗時（nav=fixed/out-of-flow），autoplay tick 量到的文件-Y
    // 少了 ≈navHeight → 跳過、不烙入 stale 值；捲回時 IO enterImmersive 分支補正（根除間歇偏移）。
    if (!isHeroIntersecting) return;
    measureOrigin(e.detail?.index);
  });

  // resize 重量（debounce 一個 rAF，避免連續 resize 抖動時過度計算）
  let resizeRaf = null;
  window.addEventListener('resize', () => {
    if (resizeRaf !== null) cancelAnimationFrame(resizeRaf);
    resizeRaf = requestAnimationFrame(measureHeight);
  });

  // top-bar 關閉會改變 hero 底緣位置 → 監看 top-bar 移除/隱藏後重量
  const topBar = document.querySelector('[data-top-bar]');
  if (topBar) {
    const topBarObserver = new MutationObserver(measureHeight);
    topBarObserver.observe(topBar, { attributes: true, attributeFilter: ['class', 'style'] });
  }

  // ----- 2. nav 三態切換：沉浸 ↔ reveal-on-scroll（方向 A） -----
  // 同一個 IO 同時驅動「沉浸態」與「實底滑入彈出」：
  //   hero 在視窗（沉浸段）→ is-immersive（隨 hero 捲走、可互動）+ 移除 is-revealed/inert
  //   hero 離開（內容段）  → 移除 is-immersive + reveal()（fixed 滑入釘頂）
  // 取代舊的 site-nav.js --sticky scroll listener（已於該檔移除，避免與 reveal 衝突）。
  if (nav && 'IntersectionObserver' in window) {
    // nav 高度負值當 rootMargin top：hero 底緣捲到「nav 下緣」時就視為離開深底 → 交接實底態
    const navHeight = Math.round(nav.getBoundingClientRect().height) || 72;

    // drawer 開著時不收回（focus 保護）；沉浸態本就可見可互動，不在此處 inert。
    const drawerOpen = () =>
      document.querySelector('.nav-toggle')?.getAttribute('aria-expanded') === 'true';

    const reveal = () => {
      nav.classList.add('is-revealed');
      nav.removeAttribute('inert');
      nav.removeAttribute('aria-hidden');
    };

    const enterImmersive = () => {
      // 沉浸態：可見可互動，絕不 inert/aria-hidden；同時撤掉 reveal 態（兩 class 互斥）
      nav.classList.add('is-immersive');
      nav.classList.remove('is-revealed');
      nav.removeAttribute('inert');
      nav.removeAttribute('aria-hidden');
    };

    const leaveImmersiveAndReveal = () => {
      nav.classList.remove('is-immersive');
      if (drawerOpen()) return; // drawer 開著：不動 reveal 態（保持可見且不 inert）
      reveal();
    };

    const heroImmersionObserver = new IntersectionObserver(
      ([entry]) => {
        isHeroIntersecting = entry.isIntersecting;
        if (entry.isIntersecting) {
          enterImmersive();
        } else {
          leaveImmersiveAndReveal();
        }
        // 治本：nav 流態切換（is-immersive=relative/in-flow ↔ is-revealed=fixed/out-of-flow）
        // 會位移 hero 的文件-Y ≈navHeight，連帶位移 FX 發散中心的文件座標基準，但此 IO 過去只換
        // class、從不重量 origin → 捲回頂端時環停在物件上方 ≈navHeight。於兩分支 class toggle 後
        // 同步重量：getBoundingClientRect 會 force reflow 取得切換後佈局 → 進 immersive 即把環鎖回
        // 物件當前位置。origin 僅經 translate / radial-gradient（合成/繪製）消費，改值不觸發 layout-shift。
        measureOrigin();
      },
      {
        threshold: 0,
        rootMargin: `-${navHeight}px 0px 0px 0px`,
      }
    );

    heroImmersionObserver.observe(heroEl);
  }
}
